import JSZip from 'jszip';

const posix = {
  dirname(p: string) {
    const i = p.lastIndexOf('/');
    return i <= 0 ? '' : p.slice(0, i);
  },
  basename(p: string) {
    const i = p.lastIndexOf('/');
    return i < 0 ? p : p.slice(i + 1);
  },
  join(...parts: string[]) {
    return parts.join('/').replace(/\/+/g, '/');
  },
  normalize(p: string) {
    const segs = p.split('/');
    const out: string[] = [];
    const abs = p.startsWith('/');
    for (const s of segs) {
      if (!s || s === '.') continue;
      if (s === '..') out.pop();
      else out.push(s);
    }
    return (abs ? '/' : '') + out.join('/');
  },
};

function looksLikeExternalUri(target: string) {
  return /^(?:[a-z]+:|\/\/|#)/i.test(target); // http:, https:, data:, mailto:, //, #
}

function absolutizeTarget(baseRelsPath: string, target: string) {
  if (!target) return target;
  if (looksLikeExternalUri(target)) return target;
  if (target.startsWith('/')) return posix.normalize(target);

  const relsDir = posix.dirname(baseRelsPath);
  const relsFile = posix.basename(baseRelsPath);
  const basePart = relsFile.replace(/\.rels$/i, '');
  const basePartPath = posix.normalize(posix.join(posix.dirname(relsDir), basePart));
  const baseDir = posix.dirname(basePartPath); // "ppt/slides"

  return posix.normalize('/' + posix.join(baseDir, target));
}

// Fix a single .rels XML string (minimal: only Relationship@Target)
function fixRelsXml(xmlStr: string, baseRelsPath: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlStr, 'application/xml');
  if (doc.querySelector('parsererror')) return xmlStr;

  let changed = false;
  doc.querySelectorAll('Relationship').forEach((rel) => {
    const mode = (rel.getAttribute('TargetMode') || '').toLowerCase();
    if (mode === 'external') return;

    const target = rel.getAttribute('Target');
    if (!target) return;

    // only handle truly relative targets
    const isRelative = !target.startsWith('/') && !looksLikeExternalUri(target);
    if (!isRelative) return;

    const abs = absolutizeTarget(baseRelsPath, target);
    if (abs !== target) {
      rel.setAttribute('Target', abs);
      changed = true;
    }
  });

  return changed ? new XMLSerializer().serializeToString(doc) : xmlStr;
}

// Scan all .rels in the zip and rewrite relative absolute
export async function fixAllRelsInZip(zip: JSZip) {
  const relsPaths = Object.keys(zip.files).filter((n) => n.endsWith('.rels'));
  for (const relsPath of relsPaths) {
    const file = zip.file(relsPath);
    if (!file) continue;
    const xml = await file.async('string');
    const fixed = fixRelsXml(xml, relsPath);
    if (fixed !== xml) {
      zip.file(relsPath, fixed);
    }
  }
}
